// Match Performance's pure computation — extracted out of MatchPerformance.js (2026-07-28) so
// the Full Physical Report's Section 3 (position-group table per selected match) can reuse the
// exact same scenario/target math instead of a third hand-copy, and so it can add a per-minute
// variant (net-new — the live MatchPerformance page has no Volume/Per-Minute toggle today).
// MatchPerformance.js imports the volume builder back in; its own behavior is unchanged.
import { MATCH_TARGETS, findMatchPosition, parseTimeToMinutes } from './matchChronicShared';
import { basisOfRows } from '../../components/EstimatedMarker';

// A match's nominal duration (2x45, 4x30, 2x75, ...): for each distinct match drill title
// present (e.g. the 2 halves, or 4 quarters), take the longest total_time recorded by any
// player for that segment — the player who played the whole segment — then sum across
// segments. Works for any format without hardcoding a segment count, since no single player
// necessarily plays every segment in full (subs/rotation).
export function computeSessionDurationMinutes(drillsForSession) {
  const maxByTitle = {};
  drillsForSession.forEach(d => {
    const mins = parseTimeToMinutes(d.total_time);
    const key = d.drill_title;
    if (!maxByTitle[key] || mins > maxByTitle[key]) maxByTitle[key] = mins;
  });
  return Object.values(maxByTitle).reduce((s, v) => s + v, 0);
}

// Matches longer than a standard 2x45 (plus a little buffer for stoppage time, up to 119 min
// before this kicks in) get their cumulative totals rescaled to a 90-minute equivalent so
// different formats (4x30, 2x75, extra time, ...) stay comparable match to match. Matches
// shorter than 90 minutes get the same treatment in the other direction (confirmed 2026-08-01
// — a shortened friendly, e.g. 75 minutes, should scale UP to a 90-min-equivalent the same way
// a long match scales down, not sit unscaled just because it fell under the old one-sided
// >119 threshold). The 90-119 band itself is deliberately left alone at a flat 1× — normal
// stoppage-time variance on an actual 90-minute match shouldn't get nudged by this at all.
export function scaleFactorForDuration(durationMinutes) {
  return (durationMinutes > 0 && (durationMinutes < 90 || durationMinutes > 119)) ? 90 / durationMinutes : 1;
}

// Per-player rows for one match session — volume totals (TD/HID/SD/HMLD/SA/Expl/Max Speed/
// Mins), %-of-target and green-flag fields (same math as MatchPerformance.js's live table),
// plus a sibling per-minute field set (value / mins) for the report's Per-Minute table.
// `positions` (optional, defaults to []): date-scoped player_positions rows — a player's target
// AND which position-group table they show up in is resolved as of matchDate, not their current
// players.position, so a player who's moved between positions during the season still compares
// correctly against whichever position he actually played each match.
export function computeMatchPerformanceRows(drillsForSession, players, matchDate, scenarios, customTemplates, positions = []) {
  const byPlayer = {};
  (drillsForSession || []).forEach(d => {
    if (!byPlayer[d.player_name]) byPlayer[d.player_name] = [];
    byPlayer[d.player_name].push(d);
  });

  return Object.entries(byPlayer).map(([name, drills]) => {
    const player = players.find(p => p.name?.toLowerCase() === name.toLowerCase());
    const pos    = findMatchPosition(positions, player?.id, matchDate, player?.position);
    // Match-day targets are scenario-independent — always the single MATCH_TARGETS
    // table for the position, regardless of which training scenario is assigned.
    const tgt = pos ? MATCH_TARGETS[pos] || null : null;

    // KNVB-estimated players (2026-10-24): their half rows carry is_estimated + estimation_basis
    // (GET /api/drills/match). A metric the estimate didn't produce is NULL on every row — it
    // stays null here (rendered "niet geschat"), never summed as 0. Measured rows are unchanged:
    // a null there still counts as 0, as before.
    const estimate = drills.some(d => d.is_estimated) ? basisOfRows(drills) || {} : null;
    const sum = key => {
      if (estimate && drills.every(d => d[key] == null)) return null;
      return drills.reduce((s, d) => s + (d[key] || 0), 0);
    };
    const td   = sum('td');
    const hid  = sum('hid');
    const sd   = sum('sd');
    const hmld = sum('hmld');
    const sa   = sum('sa');
    const expl = sum('expl');
    const maxSpd = estimate && drills.every(d => d.max_speed == null) ? null : Math.max(...drills.map(d => d.max_speed || 0));
    const mins = drills.reduce((s, d) => s + parseTimeToMinutes(d.total_time), 0);

    // Targets pro-rate DOWN for a short outing but never UP past the 90-min target —
    // stoppage/extra time shouldn't raise the bar a player is judged against.
    const targetScale = Math.min(mins, 90) / 90;

    const pct = (actual, target) =>
      actual != null && target && mins > 0 ? actual / (target * targetScale) * 100 : null;

    // Green tracks the DISPLAYED percentage (fPct rounds), so a cell reading "100%" is always
    // green — 99.6% rounds to "100%" and would otherwise sit uncoloured beside a true 100%.
    const atLeast = (actual, target) => {
      const p = pct(actual, target);
      return p != null && Math.round(p) >= 100;
    };

    const perMin = v => v != null && mins > 0 ? v / mins : null;

    return {
      name, pos,
      td, hid, sd, hmld, sa, expl, maxSpd, mins,
      isEstimated: !!estimate, estimate,
      tdPct:   pct(td,   tgt?.TD),
      hidPct:  pct(hid,  tgt?.HID),
      sdPct:   pct(sd,   tgt?.SD),
      hmldPct: pct(hmld, tgt?.HMLD),
      saPct:   pct(sa,   tgt?.SA),
      tdGreen:   atLeast(td,   tgt?.TD),
      hidGreen:  atLeast(hid,  tgt?.HID),
      sdGreen:   atLeast(sd,   tgt?.SD),
      hmldGreen: atLeast(hmld, tgt?.HMLD),
      saGreen:   atLeast(sa,   tgt?.SA),
      tdPerMin:   perMin(td),
      hidPerMin:  perMin(hid),
      sdPerMin:   perMin(sd),
      hmldPerMin: perMin(hmld),
      saPerMin:   perMin(sa),
    };
  }).sort((a, b) => (b.hmld ?? -1) - (a.hmld ?? -1));
}

// Team totals over computeMatchPerformanceRows' rows, null-aware (2026-10-24): a metric that some
// estimated players don't have is summed over the others, and notEstimated[metric] says how many
// were left out, so the UI can say so instead of silently counting a zero. maxSpd only from
// measured players — an estimated max speed is never a team best.
export const TOTAL_METRICS = ['td', 'hid', 'sd', 'hmld', 'sa', 'expl'];
export function estimateCounts(rows) {
  const est = (rows || []).filter(r => r.isEstimated);
  return {
    nEstimated: est.length,
    notEstimated: Object.fromEntries(TOTAL_METRICS.map(k => [k, est.filter(r => r[k] == null).length])),
  };
}
